/**
 * Curated PACT / website semantic color tokens exposed to Sanity CMS and
 * Discovery frontend components.
 *
 * Values are CSS custom property names (including the `--` prefix) so stored
 * document data stays bound to design tokens — e.g. `var(--color-surface-inverse)`.
 *
 * Hover / pressed variants and raw academy / Mantine ramps are intentionally
 * omitted to keep the authoring UI focused on semantic brand choices.
 */
export const cmsBrandColors = [
	// Surfaces
	{title: "Surface default", value: "--color-surface-default"},
	{title: "Surface subtle", value: "--color-surface-subtle"},
	{title: "Surface muted", value: "--color-surface-muted"},
	{title: "Surface sunken", value: "--color-surface-sunken"},
	{title: "Surface inverse", value: "--color-surface-inverse"},
	{title: "Accent yellow", value: "--color-surface-accent-yellow"},
	{title: "Accent pink", value: "--color-surface-accent-pink"},
	{title: "Accent blue", value: "--color-surface-accent-blue"},

	// Backgrounds (buttons / fills)
	{title: "Accent red", value: "--color-surface-accent-red"},
	{title: "Primary filled", value: "--color-background-primary-filled"},
	{title: "Primary light", value: "--color-background-primary-light"},
	{title: "Paper filled", value: "--color-background-paper-filled"},
	{title: "Paper light", value: "--color-background-paper-light"},
	{title: "Graph filled", value: "--color-background-graph-filled"},
	{title: "Graph light", value: "--color-background-graph-light"},
	{title: "Eraser filled", value: "--color-background-eraser-filled"},
	{title: "Eraser light", value: "--color-background-eraser-light"},
	{title: "Warning filled", value: "--color-background-warning-filled"},
	{title: "Success filled", value: "--color-background-success-filled"},
	{title: "Error filled", value: "--color-background-error-filled"},
	{title: "Gray filled", value: "--color-background-gray-filled"},
	{title: "Gray light", value: "--color-background-gray-light"},

	// Text
	{title: "Text primary", value: "--color-text-primary"},
	{title: "Text secondary", value: "--color-text-secondary"},
	{title: "Text muted", value: "--color-text-muted"},
	{title: "Text inverse", value: "--color-text-inverse"},
	{title: "Text on accent", value: "--color-text-on-accent"},

	// Borders
	{title: "Eraser border", value: "--color-border-eraser-default"},
	{title: "Graph border", value: "--color-border-graph-default"},
	{title: "Warning border", value: "--color-border-warning-default"},
	{title: "Primary border", value: "--color-border-primary-default"},
] as const;

export type CmsBrandColor = (typeof cmsBrandColors)[number];
export type CmsBrandColorToken = CmsBrandColor["value"];

export const cmsBrandColorTokens = cmsBrandColors.map(
	(color) => color.value
) as readonly CmsBrandColorToken[];

/**
 * Wrap a stored brand-color token for use in CSS / inline styles.
 */
export function cmsBrandColorCssVar(
	token: string | undefined | null
): `var(${string})` | undefined {
	if (!token) {
		return undefined;
	}
	return `var(${token})`;
}

/**
 * Background token → preferred text token on that surface.
 * Unmapped backgrounds should keep the component's default text color.
 */
const cmsBrandTextOnBackground = {
	"--color-surface-inverse": "--color-text-inverse",
} as const satisfies Partial<Record<CmsBrandColorToken, CmsBrandColorToken>>;

function cmsBrandTextTokenForBackground(
	backgroundToken: string | undefined | null
): CmsBrandColorToken | undefined {
	if (!backgroundToken) {
		return undefined;
	}
	return cmsBrandTextOnBackground[
		backgroundToken as keyof typeof cmsBrandTextOnBackground
	];
}

export function cmsBrandTextCssVarForBackground(
	backgroundToken: string | undefined | null
): `var(${string})` | undefined {
	return cmsBrandColorCssVar(cmsBrandTextTokenForBackground(backgroundToken));
}
